/**
 * @file MeetingSharingLine.tsx
 * @brief The meeting card's one quiet line: who has this meeting.
 *
 * @details
 * Sharing v2 (DES 21-10-2026, decision 6), Boardroom and Notes alike:
 * - Owner, or a company admin on a member's meeting: share icon + small
 *   avatar stack + "Shared with Sara, Bob +2" (External people marked).
 *   Nothing when it is not shared. A click opens "Who has access"
 *   (`MeetingShareMenu`): the owner adds and removes, an admin removes.
 *   Hovering shows the names in the tooltip.
 * - Someone it is shared with (or invited to it): "Shared by Ali", not
 *   clickable (the server gives them no access list).
 * The words live in `sharingLineText` (pure, tested).
 *
 * The card around it is a link: the line stops its own clicks and keys, so
 * opening the list never opens the meeting.
 *
 * No vendor cost: the summary comes with the list (one query server-side);
 * the access list is one read when opened.
 *
 * @references
 * - scratch/claude-code-report.md "Sharing v2: Part 1" sections 2 and 3
 * - ./meetingListTools.tsx (MeetingShareMenu)
 *
 * @author DES
 * @date 21-10-2026
 */

import type { KeyboardEvent, MouseEvent } from "react";
import { Share2 } from "lucide-react";
import { PersonPhoto } from "@/components/ui/PersonPhoto";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import type { MeetingSharing } from "@/features/company-meetings/schemas";
import { cn } from "@/shared/theme/cn";
import { MeetingShareMenu } from "./meetingListTools";
import { sharingLineText } from "./sharingLine";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const stop = (e: MouseEvent | KeyboardEvent) => {
  e.stopPropagation();
};

export function MeetingSharingLine({
  meetingId,
  sharing,
  className,
}: {
  meetingId: string;
  sharing: MeetingSharing | null | undefined;
  className?: string;
}) {
  const line = sharingLineText(sharing);
  if (!line) return null;

  const text = (
    <span className="min-w-0 truncate text-[13px] font-medium text-app-text-secondary">
      {line.text}
    </span>
  );

  if (line.kind === "sharedBy") {
    return (
      <span title={line.tooltip} className={cn("flex min-w-0 items-center gap-1.5", className)}>
        <Share2 className="h-3.5 w-3.5 shrink-0 text-app-text-secondary" strokeWidth={1.5} aria-hidden />
        {text}
      </span>
    );
  }

  return (
    // Its own clicks and keys stay here: the card is a link. React events
    // bubble through the menu's portal too, so the wrapper covers both.
    <span
      className={cn("flex min-w-0", className)}
      onClick={stop}
      onKeyDown={stop}
      onPointerDown={stop}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            title={line.tooltip}
            aria-label={`${line.text}. Who has access`}
            onClick={(e) => e.preventDefault()}
            className="flex min-w-0 cursor-pointer items-center gap-1.5 rounded-full outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Share2 className="h-3.5 w-3.5 shrink-0 text-brand" strokeWidth={1.5} aria-hidden />
            {line.people.length ? (
              <span className="flex shrink-0">
                {line.people.map((p, i) => (
                  <span
                    key={`${p.userId ?? p.firstName}-${i}`}
                    className={cn(
                      "flex h-[18px] w-[18px] items-center justify-center overflow-hidden rounded-full border border-app-card text-[8px] font-bold text-app-text",
                      p.isExternal ? "bg-brand/10" : "bg-app-quaternary",
                      i > 0 && "-ml-1.5",
                    )}
                  >
                    <PersonPhoto src={p.avatarUrl} fallback={initials(p.firstName ?? "")} />
                  </span>
                ))}
              </span>
            ) : null}
            {text}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="p-1">
          <MeetingShareMenu meetingId={meetingId} />
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
